// Phase 22B — Travel Rates admin (history-preserving)
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Route as RouteIcon, PowerOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import PageHeader from "@/components/PageHeader";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function TravelRates() {
  const [rows, setRows] = useState([]);
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 200 };
      if (!showInactive) params.is_active = true;
      const r = await api.get("/travel-rates", { params });
      setRows(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [showInactive]);

  useEffect(() => {
    api.get("/lookups?category=travel_modes")
      .then(r => setModes((r.data.items || r.data || []).filter(x => x.is_active !== false)))
      .catch(e => toast.error(formatApiError(e)));
    load();
  }, [load]);

  const modeLabel = (code) => modes.find(m => m.code === code)?.label || code;

  const deactivate = async (row) => {
    try {
      await api.patch(`/travel-rates/${row.id}`, { is_active: false });
      toast.success("Deactivated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="travel-rates-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Travel Rates" }]}
        title="Travel Rates"
        subtitle="₹ per km per travel mode — legacy base for policies without an override."
        favorite={{ key: "admin.travel-rates", type: "screen", label: "Travel Rates", route: "/admin/travel-rates" }}
        testid="travelrates-page-header"
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <RouteIcon className="h-6 w-6 text-[#6D2C91]" /> Travel Rates
          </h1>
          <p className="text-xs text-neutral-500 mt-0.5 max-w-xl">
            ₹ per KM per travel mode. Editing a rate soft-deactivates the old row and inserts a new one — snapshots on existing claim items are preserved.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-neutral-600">
            <input type="checkbox" checked={showInactive}
                    onChange={e => setShowInactive(e.target.checked)}
                    data-testid="travel-rate-show-inactive" />
            Include history
          </label>
          <Button className="tulsi-gradient text-white" onClick={() => setCreating(true)}
                  data-testid="travel-rate-new">
            <Plus className="h-4 w-4 mr-1" /> New rate
          </Button>
        </div>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-0">
          <Table data-testid="travel-rates-table">
            <TableHeader>
              <TableRow>
                <TableHead>Mode</TableHead>
                <TableHead className="text-right">Rate (₹/km)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Since</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5} className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" /></TableCell></TableRow>}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-10 text-center text-neutral-400 text-sm">No rates configured.</TableCell></TableRow>
              )}
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`travel-rate-row-${r.mode_code}${r.is_active ? "" : "-inactive-" + r.id.slice(0,4)}`}>
                  <TableCell className="font-medium">{modeLabel(r.mode_code)}
                    <div className="text-[10px] font-mono text-neutral-400 mt-0.5">{r.mode_code}</div>
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold">
                    ₹ {(r.rate_per_km_inr || 0).toLocaleString("en-IN", {minimumFractionDigits: 2})}
                  </TableCell>
                  <TableCell>
                    <Badge className={r.is_active ? "bg-emerald-100 text-emerald-800" : "bg-neutral-100 text-neutral-500"}>
                      {r.is_active ? "active" : "history"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-[11px] text-neutral-500">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {r.is_active && (
                      <Button variant="ghost" size="icon" onClick={() => deactivate(r)}
                              data-testid={`travel-rate-deact-${r.mode_code}`}>
                        <PowerOff className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {creating && (
        <NewRateDialog modes={modes} onClose={() => setCreating(false)}
                        onSaved={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}

function NewRateDialog({ modes, onClose, onSaved }) {
  const [modeCode, setModeCode] = useState("");
  const [rate, setRate] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!modeCode || rate === "") { toast.error("Select mode and enter rate"); return; }
    setSaving(true);
    try {
      await api.post("/travel-rates", {
        mode_code: modeCode, rate_per_km_inr: parseFloat(rate), is_active: true,
      });
      toast.success("New rate active; previous row auto-deactivated for history.");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="travel-rate-dialog">
        <DialogHeader>
          <DialogTitle>New travel rate</DialogTitle>
          <DialogDescription>Existing active row for this mode will be soft-deactivated automatically.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Mode</Label>
            <Select value={modeCode} onValueChange={setModeCode}>
              <SelectTrigger data-testid="travel-rate-mode"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {modes.map(m => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Rate (₹/km)</Label>
            <Input type="number" min="0" step="0.01" value={rate}
                   onChange={e => setRate(e.target.value)}
                   data-testid="travel-rate-amount" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="tulsi-gradient text-white" onClick={save} disabled={saving}
                  data-testid="travel-rate-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
